import { formatEuro } from "@/lib/helpers";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table } from 'lucide-react';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { useTableauPrelevements } from '@/hooks/api/use-payments';
import { formatDate } from '@/lib/dates';

interface TableauPrevelementsProps {
    /** Dernière quittance du contrat (non utilisée : le tableau couvre tous les prélèvements à venir). */
    quittance_id?: number | string;
    contract_id?: string;
}

const TableauPrevelements = ({ contract_id }: TableauPrevelementsProps) => {
    // On ne récupère plus les prélevements de la derniere quittance mais la somme des prels à venir du contrat
    const prelevementsQuery = useTableauPrelevements(contract_id);
    const prelevements = prelevementsQuery.data?.tableau ?? [];

    // Comme avant : un échec de lecture laisse le spinner affiché.
    if (prelevementsQuery.isPending || prelevementsQuery.isError) return <SmallSpinner />;

    return (
        <div className="space-y-6 mt-6">
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Table className="w-5 h-5 text-primary" />
                        Tableau de prélèvement
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    {prelevements.length >= 1 ? (
                        <>
                            {/* Desktop */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-sm" aria-label="Tableau de prélèvement">
                                    <thead>
                                        <tr className="border-b">
                                            <th scope="col" className="text-left py-4 px-6 font-semibold text-muted-foreground">Date</th>
                                            <th scope="col" className="text-left py-4 px-6 font-semibold text-muted-foreground">Montant à prélever</th>
                                            <th scope="col" className="text-left py-4 px-6 font-semibold text-muted-foreground">Effectué le</th>
                                            <th scope="col" className="text-left py-4 px-6 font-semibold text-muted-foreground">Motif du rejet</th>
                                            <th scope="col" className="text-left py-4 px-6 font-semibold text-muted-foreground">Date du rejet</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {prelevements.map((prelevement, i) => (
                                            <tr key={i} className="border-b last:border-0 transition-colors hover:bg-muted/50">
                                                <td className="py-4 px-6 font-medium">{formatDate(prelevement.date)}</td>
                                                <td className="py-4 px-6 font-medium">{formatEuro(prelevement.montant)}</td>
                                                <td className="py-4 px-6">
                                                    {prelevement.datePrelevement ? (
                                                        <span className="font-medium">{prelevement.datePrelevement}</span>
                                                    ) : (
                                                        <div>
                                                            <Badge variant="secondary">A venir</Badge>
                                                            <p className="text-xs text-muted-foreground mt-1">Le {formatDate(prelevement.date)}</p>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="py-4 px-6">
                                                    {prelevement.motifRejet ? (
                                                        <Badge variant="destructive">{prelevement.motifRejet}</Badge>
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </td>
                                                <td className="py-4 px-6 font-medium">
                                                    {prelevement.dateRejet ? formatDate(prelevement.dateRejet) : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile */}
                            <div className="md:hidden space-y-3">
                                {prelevements.map((prelevement, i) => (
                                    <div key={i} className="p-6 rounded-lg border bg-muted/30">
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div>
                                                <p className="text-muted-foreground">Date</p>
                                                <p className="font-medium">{formatDate(prelevement.date)}</p>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Montant</p>
                                                <p className="font-medium">{formatEuro(prelevement.montant)}</p>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Effectué le</p>
                                                {prelevement.datePrelevement ? (
                                                    <p className="font-medium">{prelevement.datePrelevement}</p>
                                                ) : (
                                                    <Badge variant="secondary">A venir</Badge>
                                                )}
                                            </div>
                                            {prelevement.motifRejet && (
                                                <div>
                                                    <p className="text-muted-foreground">Rejet</p>
                                                    <Badge variant="destructive">{prelevement.motifRejet}</Badge>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground">Aucun prélèvement n'a été trouvé.</p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default TableauPrevelements;
